import * as d3 from 'd3'
import {getGoogleColorByString} from '@/utils/d3Utils'

export default function StackedAreaChart(d, options) {
    const cfg = {
        isDiscrete: true,
        maxZoom: 2,
        pointsLimit: 10,
        xAxis: "x"
    };

    copyOptions(options);

    function copyOptions(options) {
        if ('undefined' !== typeof options)
            for (let i in options) if (options.hasOwnProperty(i) && 'undefined' !== typeof options[i])
                cfg[i] = options[i];
    }

    const data = JSON.parse(JSON.stringify(d));

    data.sort((a, b) => a[cfg.xAxis] - b[cfg.xAxis]);

    const container = d3.select(options.container);

    container.selectAll("*").remove();

    const labelXOffset = 20;
    const width = cfg.width - cfg.margin.left - cfg.margin.right;
    let height = cfg.height - cfg.margin.top - cfg.margin.bottom;

    if (data.length < 2) {
        container.append('div')
            .style("display", "table-cell")
            .style("width", width + 'px')
            .style("height", height + 'px')
            .style("text-align", 'center')
            .style("vertical-align", 'middle')
            .style("font-size", '14pt')
            .text("Нет данных");
        return;
    }

    let height2 = height / 4 - labelXOffset;

    height -= height2 + labelXOffset * 2;

    const x = d3.scaleLinear().range([0, width]);
    const x2 = d3.scaleLinear().range([0, width]);
    const y = d3.scaleLinear().range([height, 0]);
    const y2 = d3.scaleLinear().range([height2, 0]);

    const color = getGoogleColorByString;

    const xAxis = d3.axisBottom().scale(x);

    xAxis.tickFormat(d => d);

    const xAxis2 = d3.axisBottom().scale(x2);

    xAxis2.tickFormat(d => d);

    const yAxis = d3.axisLeft().scale(y);

    let stackData;

    let minX = data[0][cfg.xAxis];
    let maxX = data[data.length - 1][cfg.xAxis];

    xAxis2.tickValues(generateTicks(minX, maxX));


    function generateTicks(minX, maxX) {
        const ticks = [];
        data.forEach(d => {
            if (d[cfg.xAxis] >= minX && d[cfg.xAxis] <= maxX) ticks.push(d[cfg.xAxis]);
        });
        const step = Math.round(ticks.length / cfg.pointsLimit);

        if (step <= 1) return ticks;
        let newTicks = [];
        for (let i = 0; i < ticks.length; i += step) newTicks.push(ticks[i]);

        return newTicks;
    }

    const brush = d3.brushX()
        .extent([[0, 0], [width, height2]])
        .on("brush start", () => {
            graphic.selectAll(".marker").remove();
        })
        .on("brush end", () => {
            if (d3.event.sourceEvent && d3.event.sourceEvent.type === "zoom") return;
            const s = d3.event.selection || x2.range();
            const domain = s.map(x2.invert, x2);

            minX = domain[0];
            maxX = domain[1];
            x.domain(domain);
            g.selectAll(".area").attr("d", area);

            if (cfg.isDiscrete) xAxis.tickValues(generateTicks(minX, maxX));

            g.select(".x.axis").call(xAxis);
            svg.select(".zoom").call(zoom.transform, d3.zoomIdentity
                .scale(width / (s[1] - s[0]))
                .translate(-s[0], 0));

            onGraphicMouseMove();
            drawMarkers(data);
        });

    function zoomChart(minX, maxX) {
        if (Math.ceil(maxX) - Math.ceil(minX) < cfg.maxZoom) return;
        x.domain([minX, maxX]);
        g.selectAll(".area").attr("d", area);

        if (cfg.isDiscrete) xAxis.tickValues(generateTicks(minX, maxX));

        g.select(".x.axis").call(xAxis);

        const dataMinX = data[0][cfg.xAxis];
        const dataMaxX = data[data.length - 1][cfg.xAxis];

        context.select(".brush").call(brush.move, [width / (dataMaxX - dataMinX) * (minX - dataMinX), width / (dataMaxX - dataMinX) * (maxX - dataMinX)]);
        drawMarkers(data);
    }

    const zoom = d3.zoom()
        .scaleExtent([1, Infinity])
        .translateExtent([[0, 0], [width, height]])
        .extent([[0, 0], [width, height]])
        .on("zoom", () => {
            if (d3.event.sourceEvent && d3.event.sourceEvent.type === "brush") return;
            const t = d3.event.transform;
            const domain = t.rescaleX(x2).domain();
            minX = domain[0];
            maxX = domain[1];
            zoomChart(minX, maxX);
            onGraphicMouseMove();
        });

    const area = d3.area()
        .x(d => x(d.data[cfg.xAxis]))
        .y0(d => y(d[0]))
        .y1(d => y(d[1]));

    const area2 = d3.area()
        .x(d => x2(d.data[cfg.xAxis]))
        .y0(d => y2(d[0]))
        .y1(d => y2(d[1]));

    const stack = d3.stack();

    const bisectDate = d3.bisector(d => d[cfg.xAxis]).left;

    const svg = container.append("svg")
        .attr("width", cfg.width)
        .attr("height", cfg.height);

    const g = svg.append("g")
        .attr("transform",
            "translate(" + cfg.margin.left + "," + cfg.margin.top + ")");

    const context = svg.append("g")
        .attr("class", "context")
        .attr("transform", "translate(" + cfg.margin.left + "," + (cfg.margin.top + height + labelXOffset * 2) + ")");

    const extent = d3.extent(data, d => d[cfg.xAxis]);

    x.domain(extent);
    x2.domain(extent);

    let maxY = d3.max(data, d => d3.sum(d3.keys(d).map(key => key !== 'x' ? d[key] : 0)));
    let minY = d3.min(data, d => d3.sum(d3.keys(d).map(key => key !== 'x' ? d[key] : 0)));


    if (maxY > 0 && minY > 0) minY = 0;
    else if (maxY < 0 && minY < 0) maxY = 0;

    maxY *= 1.2;

    y.domain([minY, maxY]);
    y2.domain([minY, maxY]);

    svg.append("defs").append("clipPath")
        .attr("id", "clip")
        .append("rect")
        .attr("width", width)
        .attr("height", height);

    const tooltip = container.append("div").attr("class", "toolTip blackTooltip");
    const labelTooltip = container.append("div").attr("class", "toolTip whiteTooltip");

    draw(data);
    drawMarkers(data);

    if (data.length > cfg.pointsLimit)
        zoomChart(data[data.length - 1 - cfg.pointsLimit][cfg.xAxis], data[data.length - 1][cfg.xAxis]);

    var graphic;
    var zoomRect;

    function drawMarker(x, y, label) {
        graphic.append("path")
            .style("stroke", "#6435C9")
            .style("fill", "#6435C9")
            .attr("d", "M " + (x + 5) + "," + (y - 14) + ",L " + (x - 5) + "," + (y - 14) +
                ",L " + (x - 5) + "," + (y - 7) + ", L " + (x) + "," + (y) +
                ", L " + (x + 5) + "," + (y - 7) + " Z").attr("class", "marker")
            .on("mouseover", function () {
                drawLabelTooltip(label, x, y);
            })
            .on("mouseout", function () {
                labelTooltip.style("display", "none");
            });

        function drawLabelTooltip(label, x, y) {
            labelTooltip.selectAll("*").remove();
            labelTooltip.append("div").text(label);
            labelTooltip.style("display", "block");
            const tooltipBoundingClientRect = labelTooltip._groups[0][0].getBoundingClientRect();
            labelTooltip.style("left", (x) + "px").style("top", (y - tooltipBoundingClientRect.height) + "px");

        }
    }

    function onGraphicMouseMove() {
        if (!zoomRect) return;
        const mouseX = d3.mouse(zoomRect)[0];
        let x0 = x.invert(mouseX),
            i = bisectDate(data, x0, 1),
            d0 = data[i - 1],
            d1 = data[i];

        if (x0 - d0[cfg.xAxis] < d1[cfg.xAxis] - x0) {
            i--;
            d1 = d0;
        }

        const lineX = (cfg.isDiscrete) ? width / (maxX - minX) * (d1[cfg.xAxis] - minX) : mouseX;

        if (lineX > width || lineX < 0) return;

        const points = [];

        drawTooltip(i);
        drawLine(i);

        function drawLine(index) {
            removeLine();
            tooltip.style("display", "block");
            graphic.insert("line", "#rect").attr("x1", lineX).attr("y1", 0).attr("x2", lineX)
                .attr("y2", height).attr("stroke-width", 1).attr("stroke", "black");

            for (let i = 0; i < points.length; i++)
                graphic.insert("circle", "#rect").style("fill", "#000").attr("cx", lineX).attr("cy", y(points[i])).attr("r", 3);
        }


        function drawTooltip(index) {
            tooltip.selectAll("*").remove();

            const event = d3.event;

            let data = stackData[0][index].data;

            if (!cfg.tooltipTemplate) return;

            createBody(tooltip.append("div").style("border-bottom", "1px solid #ffffff"), cfg.tooltipTemplate.header, data);


            cfg.tooltipTemplate.items.forEach(itemTemplate => {
                createBody(tooltip.append("div"), itemTemplate, data);
            });

            if (data.tooltip) {
                tooltip.append("div").text(data.tooltip.header).style("border-bottom", "1px solid #ffffff").style("margin-top", "5pt");
                data.tooltip.body.forEach(s => {
                    if (typeof s === 'object') {
                        let line = tooltip.append("div");
                        let i = 0;
                        Object.keys(s).forEach(p => {
                            let text = line.append("div").style("display", "inline-block").text(s[p]);
                            if (i > 0) text.style("margin-left", "10pt");
                            i++;
                        })
                    }
                    else
                        tooltip.append("div").text(s)
                })
            }

            let sum = 0;

            for (let prop in data) {
                if (!data.hasOwnProperty(prop)) continue;
                const value = data[prop];
                if (prop !== 'x' && prop !== 'tooltip' && prop !== 'marker' && $.isNumeric(value)) {
                    sum += value;
                    points.push(sum);
                }
            }

            tooltip.style("display", "block");

            const tooltipClientRect = tooltip._groups[0][0].getBoundingClientRect();

            let tooltipX = (cfg.isDiscrete) ? lineX + tooltipClientRect.width / 2 + 10 : event.pageX + 10;
            let tooltipY = (cfg.isDiscrete) ? height / 2 - tooltipClientRect.height / 2 : event.pageY - tooltipClientRect.height - 30;

            if (tooltipX < width / 2) {
                tooltip.style("left", tooltipX + "px").style("top", tooltipY + "px");
            } else {
                tooltip.style("left", `calc(${tooltipX}px - ${tooltipClientRect.width + 15}px)`).style("top", tooltipY + "px");
            }

            function calcY(x1, y1, x2, y2, x) {
                const y = (x - x1) / (x2 - x1) * (y2 - y1) + y1;
                return isNaN(y) ? y1 : y;
            }

            function createBody(parent, template, data) {
                const templateSplit = template.split("%%");
                for (let i = 0; i < templateSplit.length; i += 2) {
                    let s = templateSplit[i];
                    let div;
                    if (s) {
                        div = parent.append("div").style("display", "inline-block");
                        div.text(s);
                        if (i > 0)
                            div.style("margin-left", "10pt");
                    }
                    if (i + 1 >= templateSplit.length) continue;
                    let value = data[templateSplit[i + 1]];
                    if ('undefined' === typeof value) continue;
                    if (!Number.isInteger(value) && $.isNumeric(value)) {
                        value = Number(value).toFixed(3);
                    }

                    div = parent.append("div").style("display", "inline-block");
                    div.text(value);
                    div.style("margin-left", "10pt");
                }
            }
        }
    }

    function drawMarkers(data) {
        graphic.selectAll(".marker").remove();
        data.forEach(d => {
            if (!d.marker || d[cfg.xAxis] < minX || d[cfg.xAxis] > maxX) return;
            let x = width / (maxX - minX) * (d[cfg.xAxis] - minX);
            let y = 0;

            for (let key in d) {
                if (key !== 'x' && key !== 'tooltip' && key !== 'marker') {
                    y += (height - 5) / (maxY - minY) * (maxY - d[key]);
                    drawMarker(x, y, d.marker);
                }
            }
        })
    }

    function removeLine() {
        graphic.selectAll("line").remove();
        graphic.selectAll("circle").remove();
        tooltip.style("display", "none");
    }

    function draw(data) {
        stack.keys(d3.keys(data[0]).filter(key => key !== 'x' && key !== 'tooltip' && key !== 'marker'));

        stackData = stack(data);

        const path1 = g.selectAll('.browser')
            .data(stackData)
            .enter().append('g')
            .attr('class', d => 'browser ' + d.key)
            .attr('fill-opacity', 1)
            .append('path')
            .attr('class', 'area')
            .attr('d', area);

        if (cfg.fill)
            path1.style('fill', d => color(d.key));
        else {
            path1.attr('stroke', d => color(d.key))
                .attr('stroke-width', 2)
                .style('fill', 'none');

            drawRect(g, 0, height - 2, width, 4, 'white');
            drawRect(g, width - 2, 0, 2, height, 'white');
            if (minY < 0) drawRect(g, 0, (height - 5) / (maxY - minY) * maxY + 4, width - 2, 2, 'black');
        }

        g.append('g')
            .attr('class', 'x axis')
            .attr('transform', 'translate(0,' + height + ')')
            .call(xAxis);

        g.append('g')
            .attr('class', 'y axis')
            .call(yAxis);

        g.append("text")
            .attr("text-anchor", "middle")
            .attr("transform", "translate(" + (-cfg.margin.left + 20) + "," + (height / 2) + ")rotate(-90)")
            .text(cfg.axisLabels.y);

        g.append("text")
            .attr("text-anchor", "middle")
            .attr("transform", "translate(" + width / 2 + "," + (height + cfg.margin.top + labelXOffset) + ")")
            .text(cfg.axisLabels.x);


        graphic = svg.append("g");

        graphic.attr("transform", "translate(" + cfg.margin.left + "," + cfg.margin.top + ")")
            .append("rect").attr("id", "rect")
            .attr("class", "zoom")
            .attr("width", width)
            .attr("height", height)
            .call(zoom)
            .on("mousemove", function () {
                zoomRect = this;
                onGraphicMouseMove();
            });

        container.on("mouseleave", () => {
            removeLine();
        });

        const path2 = context.selectAll('.browser')
            .data(stackData)
            .enter().append('g')
            .attr('class', d => 'browser ' + d.key)
            .attr('fill-opacity', 1)
            .append('path')
            .attr('class', 'area')
            .attr('d', area2);

        if (cfg.fill)
            path2.style('fill', d => color(d.key));
        else {
            path2.attr('stroke', d => color(d.key))
                .attr('stroke-width', 2)
                .style('fill', 'none');

            drawRect(context, 0, height2 - 2, width, 4, 'white');
            drawRect(context, width - 2, 0, 2, height2, 'white');
            drawRect(context, 0, 0, 2, height2, 'white');

            if (minY < 0) drawRect(context, 0, height2 / (maxY - minY) * maxY + 2, width - 2, 2, 'black');
        }

        context.append('g')
            .attr('class', 'x axis')
            .attr('transform', 'translate(0,' + height2 + ')')
            .call(xAxis2);

        const brushElement = context.append("g")
            .attr("class", "brush");

        brushElement.call(brush)
            .call(brush.move, x.range());

        brushElement.selectAll(".handle").remove();

        function drawRect(parent, x, y, width, height, color) {
            parent.append("rect").attr("x", x)
                .attr("y", y)
                .attr("width", width)
                .attr("height", height)
                .style('fill', color);
        }
    }
}
